import {
  DataGridPremiumProps,
  GridColumnMenuProps,
  GridColumnVisibilityModel,
  GridGroupingColDefOverride,
  GridGroupingColDefOverrideParams,
  GridSortModel,
  GridPinnedColumns,
  useGridApiRef,
  GridRowSelectionModel,
  GridRowModesModel,
  GridFilterModel,
} from "@mui/x-data-grid-premium";
import React, {
  CSSProperties,
  JSXElementConstructor,
  Dispatch,
  SetStateAction,
  useState,
  ForwardRefExoticComponent,
  RefAttributes,
} from "react";
import { initialFilterProps } from "src/interfaces/tables/tables";
import { useSettingsContext } from "../../useSettings";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { useTableEdits } from "./useTableEdits";
import { useHeaderFilter } from "./useHeaderFilter";
import {
  TableDialogHandle,
  TableDialogPayload,
} from "src/components/molecules/table/TableDialog";
import {
  useTableVisibility,
  useRowsLoadingFlag,
  useInitialUrlFilter,
  useRowSelectionHandler,
  useTableDialog,
  createRowClassName,
  createRowIdGetter,
  useGroupExpansionState,
} from "./useTableSettingsHelpers";

export const defaultCompactDensity = 20;
export const defaultTableDensity = 45;
export const defaultComfortableDensity = 65;
export const defaultGroupingExpansionDepth = 3;

export interface initialTableSettings {
  density?: number;
  allRows?: any;
  addRow?: (newRow: any) => void;
  setDensity?: Dispatch<SetStateAction<number>>;
  groupingColDef?:
  | null
  | GridGroupingColDefOverride<any>
  | ((params: GridGroupingColDefOverrideParams) => GridGroupingColDefOverride<any>);
  checkboxSelection?: boolean;
  isRowSelectable?: DataGridPremiumProps<any>['isRowSelectable'];
  loading?: boolean;
  setLoading?: Dispatch<SetStateAction<boolean>>;
  idPath?: string[];
  apiRef?: React.MutableRefObject<GridApiPremium>;
  treeData?: boolean;
  noRowsMessage?: JSXElementConstructor<any>;
  initalSorting?: null | GridSortModel;
  hideFooter?: boolean;
  hasToolbar?: boolean;
  hideFooterRowCount?: boolean;
  disableSelectionOnClick?: boolean;
  selectOnlyOneRow?: boolean;
  autoHeight?: boolean;
  customWrapperStyle?: null | CSSProperties;
  hasUnsavedRows?: boolean;
  changedRowsRef?: React.MutableRefObject<any[]>;
  changeCounter?: number;
  originalRowsRef?: React.MutableRefObject<any[]>;
  setHasUnsavedRows?: React.Dispatch<React.SetStateAction<boolean>>;
  updateChangedRowsSetting?: () => void;
  discardAllChanges?: () => void;
  tableName?: string;
  rowModes?: any;
  setRowModes?: (mode: GridRowModesModel) => void;
  discardRowChanges?: (id: any) => void;
  commitChanges?: () => void;
  addAndPinMultiEditRow?: () => void;
  removeMultiEditRow?: () => void;
  pinnedRows?: any;
  rowSelectionModel?: GridRowSelectionModel;
  setRowSelectionModel?: React.Dispatch<React.SetStateAction<GridRowSelectionModel>>;
  handleRowSelectionModelChange?: (
    newSelectionModel: GridRowSelectionModel,
  ) => void;
  deleteRow?: (id: any) => void;
  quickFilterValues?: any;
  setQuickFilterValues?: React.Dispatch<React.SetStateAction<any>>;
  filterModel?: GridFilterModel;
  setFilterModel?: React.Dispatch<React.SetStateAction<GridFilterModel>>;
  headerFilter?: boolean;
  setHeaderFilter?: React.Dispatch<React.SetStateAction<boolean>>;
  processRowUpdateRef?: React.MutableRefObject<
    ((newRow: any, oldRow: any) => any) | null
  >;

  getDetailPanelContent?: (row: any) => React.ReactNode;
  detailPanelExpandedByDefault?: boolean;
  tableDialog?: {
    ref: React.RefObject<TableDialogHandle>;
    open: (p: TableDialogPayload) => void;
    update: (p: Partial<TableDialogPayload>) => void;
    close: () => void;
  };

  // NOTE: signature matches MUI's expectation (node => boolean)
  isGroupExpandedByDefault?: (node: any) => boolean;
  getRowClassName?: (params: any) => string;
  getRowId?: (row: any) => any;

  status?: number;
  setExternalRows?: Dispatch<SetStateAction<any[]>>;
  editMode?: "row" | "cell";
}

export interface tableSettingsProps extends initialTableSettings {
  columnVisibilityModel?: GridColumnVisibilityModel;
  setColumnVisibilityModel?: React.Dispatch<
    React.SetStateAction<GridColumnVisibilityModel>
  >;
  initialFilter?: initialFilterProps;
  defaultGroupingExpansionDepth?: number;
  defaultExpanded?: boolean;
  customColumnMenu?: ForwardRefExoticComponent<
    GridColumnMenuProps & RefAttributes<HTMLUListElement>
  >;
  pinnedColumns?: GridPinnedColumns;
  setPinnedColumns?: React.Dispatch<React.SetStateAction<GridPinnedColumns>>;
  handlePinnedColumnsChange?: (updatedPinnedColumns: any, details?: any) => void;
}

const getDefaultConfig = () => ({
  tableName: "UnnamedTable",
  idPath: ["Id"],
  checkboxSelection: false,
  treeData: false, // default – overridden by config.treeData when provided
  noRowsMessage: "No rows",
  initalSorting: null,
  hideFooter: true,
  hasToolbar: true,
  hideFooterRowCount: true,
  disableSelectionOnClick: false,
  selectOnlyOneRow: false,
  autoHeight: false,
  customWrapperStyle: null,
  setHasUnsavedRows: null,
  updateChangedRowsSetting: null,
  groupingColDef: null,
  originalRowsRef: null,
  changeCounter: null,
  editMode: "row" as const,

  // NEW default: do NOT expand detail panels unless explicitly enabled
  detailPanelExpandedByDefault: false,
});

export const useInitialTableSettings = (additionalConfig, rows = []) => {
  const defaultConfig = getDefaultConfig();
  // config.treeData is now the source of truth; defaults to false above
  const config = { ...defaultConfig, ...additionalConfig };

  const [loading, setLoading] = useState<boolean>(
    rows?.length > 0 ? false : true,
  );
  const { settings, setSetting } = useSettingsContext();

  const apiRef = useGridApiRef();

  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);

  const {
    allRows,
    changedRowsRef,
    hasUnsavedRows,
    discardAllChanges,
    commitChanges,
    discardRowChanges,
    addRow,
    rowModes,
    setRowModes,
    addAndPinMultiEditRow,
    removeMultiEditRow,
    pinnedRows,
    deleteRow,
    processRowUpdateRef,
  } = useTableEdits(
    apiRef,
    rows,
    config?.tableName,
    config?.idPath,
    rowSelectionModel,
    config?.setExternalRows,
  );

  const handleRowSelectionModelChange = useRowSelectionHandler(
    config?.selectOnlyOneRow,
    rowSelectionModel,
    setRowSelectionModel,
    removeMultiEditRow,
  );

  const [density, setDensity] = useState(
    settings?.TableDensity ?? defaultTableDensity,
  );
  const [quickFilterValues, setQuickFilterValues] = useState("");
  const [filterModel, setFilterModel] = useState<GridFilterModel>(null);

  const tableDialog = useTableDialog();

  const { headerFilterSetting, setHeaderFilter } = useHeaderFilter(
    settings,
    config,
    setSetting,
  );

  const tableSettings: initialTableSettings = {
    ...config,
    allRows,
    addRow,
    density,
    setDensity,
    discardAllChanges,
    commitChanges,
    loading,
    setLoading,
    apiRef,
    hasUnsavedRows,
    changedRowsRef,
    rowModes,
    setRowModes,
    discardRowChanges,
    addAndPinMultiEditRow,
    removeMultiEditRow,
    pinnedRows,
    rowSelectionModel,
    setRowSelectionModel,
    handleRowSelectionModelChange,
    deleteRow,
    quickFilterValues,
    setQuickFilterValues,
    setFilterModel,
    filterModel,
    headerFilter: headerFilterSetting,
    setHeaderFilter,
    processRowUpdateRef,
    tableDialog,
    getDetailPanelContent: config?.getDetailPanelContent,
    detailPanelExpandedByDefault: Boolean(config?.detailPanelExpandedByDefault),
    status: config?.status,
    editMode: config?.editMode ?? "row",
  };

  return tableSettings;
};

export const useCompleteTableSettings = (config, initialTableSettings) => {
  const { columns } = config || {};
  const { setLoading, tableName, apiRef, treeData, allRows } = initialTableSettings;

  const {
    pinnedColumns,
    setPinnedColumns,
    handlePinnedColumnsChange,
    columnVisibilityModel,
    updateColumnVisibilityModel,
    tableExpanded,
  } = useTableVisibility(columns, tableName);

  useRowsLoadingFlag(initialTableSettings.allRows, setLoading);

  const initalFilter = useInitialUrlFilter(initialTableSettings);

  const isGroupExpandedByDefault =
    treeData && apiRef
      ? useGroupExpansionState(apiRef, tableExpanded, treeData, allRows)
      : undefined;

  const getRowClassName = createRowClassName(initialTableSettings);
  const getRowId = createRowIdGetter(initialTableSettings);

  const tableSettings: tableSettingsProps = {
    ...initialTableSettings,
    pinnedColumns,
    setPinnedColumns,
    handlePinnedColumnsChange,
    customColumnMenu: null,
    defaultGroupingExpansionDepth: 3,
    defaultExpanded: tableExpanded,
    columnVisibilityModel,
    setColumnVisibilityModel: updateColumnVisibilityModel,
    initailFilter: initalFilter,
    tableName,
    isGroupExpandedByDefault,
    getRowClassName,
    getRowId,
  };

  return tableSettings;
};

export const useTableSettings = (config) => {
  const { columns, rows = [], tableName = "UnnamedTable" } = config || {};

  let tableSettings = useInitialTableSettings(
    { ...config, tableName },
    rows,
  );
  tableSettings = {
    ...useCompleteTableSettings({ columns }, tableSettings),
  };

  const completeTableSettings: tableSettingsProps = { ...tableSettings };

  return completeTableSettings;
};

const extractIdFromPath = (obj, path) => {
  return path.reduce(
    (acc, key) => (acc && acc[key] ? acc[key] : undefined),
    obj,
  );
};

export const mapObjectsWithId = (arrayOfObjects, idPath) => {
  if (!arrayOfObjects?.length) return [];
  return arrayOfObjects.map((obj) => {
    const Id = extractIdFromPath(obj, idPath);
    if (Id !== undefined) return { ...obj, Id };
    return { ...obj };
  });
};